import { ViewConfig } from "@lib/views";
import { CatalogItemColumn } from "@common/types/views/catalogItemViews";
import { CatalogItemSort } from "@common/types/catalogItem";
import { CatalogItemRow } from "./catalogItems.types";
import { NameCell } from "./Cells/NameCell";
import { DescriptionCell } from "./Cells/DescriptionCell";
import { UnitPriceCell } from "./Cells/UnitPriceCell";
import { ClassCell } from "./Cells/ClassCell";
import { EntityCell } from "./Cells/EntityCell";
import { AccrualAccountCell } from "./Cells/AccrualAccountCell";
import { ReceivablesAccountCell } from "./Cells/ReceivablesAccountCell";
import { LastUpdatedCell } from "./Cells/LastUpdatedCell";
import { InvoiceCountCell } from "./Cells/InvoiceCountCell";
import { TemplateCountCell } from "./Cells/TemplateCountCell";
import { ActionsCell } from "./Cells/ActionsCell";

interface CreateCatalogItemViewConfigParams {
    // Gates the Class column the same way the invoicing By line item view
    // gates its own Class column: hidden from Column Settings entirely (not
    // just visually) for workspaces without the CLASSES plan capability.
    hasClassesAccess: boolean;
    // Gates the Receivables account column: hidden from Column Settings
    // entirely for workspaces without the ACCRUAL_LEDGER plan capability
    // (e.g. Basic plan) or without any accrual ledger on a business entity.
    // Income account stays available - it is the default copied onto invoice
    // line / transaction coding.
    showReceivablesAccount: boolean;
}

export const createCatalogItemViewConfig = ({
    hasClassesAccess,
    showReceivablesAccount,
}: CreateCatalogItemViewConfigParams): ViewConfig<
    CatalogItemRow,
    CatalogItemColumn,
    CatalogItemSort
> => ({
    columns: {
        [CatalogItemColumn.NAME]: {
            accessorKey: "name",
            name: "Item",
            header: "Item",
            immutable: true,
            cell: NameCell,
            minSize: 280,
            maxSize: 600,
            sort: {
                asc: CatalogItemSort.NAME_ASC,
                desc: CatalogItemSort.NAME_DESC,
            },
        },
        [CatalogItemColumn.DESCRIPTION]: {
            accessorKey: "description",
            name: "Item description",
            header: "Item description",
            cell: DescriptionCell,
            minSize: 360,
            maxSize: 800,
        },
        [CatalogItemColumn.UNIT_PRICE]: {
            accessorKey: "unitPrice",
            name: "Rate",
            header: "Rate",
            cell: UnitPriceCell,
            minSize: 120,
            maxSize: 160,
            sort: {
                asc: CatalogItemSort.UNIT_PRICE_ASC,
                desc: CatalogItemSort.UNIT_PRICE_DESC,
            },
        },
        [CatalogItemColumn.CLASS]: {
            accessorKey: "classAssignments",
            name: "Class",
            header: "Class",
            cell: ClassCell,
            minSize: 220,
            maxSize: 220,
            internal: !hasClassesAccess,
        },
        [CatalogItemColumn.ENTITY]: {
            accessorKey: "entityId",
            name: "Entity",
            header: "Entity",
            cell: EntityCell,
            minSize: 160,
            maxSize: 280,
        },
        [CatalogItemColumn.ACCRUAL_ACCOUNT]: {
            accessorKey: "revenueAccountId",
            name: "Income account",
            header: "Income account",
            cell: AccrualAccountCell,
            minSize: 180,
            maxSize: 320,
        },
        [CatalogItemColumn.RECEIVABLES_ACCOUNT]: {
            accessorKey: "receivablesAccountId",
            name: "Receivables account",
            header: "Receivables account",
            cell: ReceivablesAccountCell,
            minSize: 180,
            maxSize: 320,
            internal: !showReceivablesAccount,
        },
        [CatalogItemColumn.LAST_UPDATED]: {
            accessorKey: "updatedAt",
            name: "Updated on",
            header: "Updated on",
            cell: LastUpdatedCell,
            minSize: 120,
            maxSize: 160,
            sort: {
                asc: CatalogItemSort.LAST_UPDATED_ASC,
                desc: CatalogItemSort.LAST_UPDATED_DESC,
            },
        },
        [CatalogItemColumn.INVOICE_COUNT]: {
            accessorKey: "invoiceCount",
            name: "Invoices",
            header: "Invoices",
            cell: InvoiceCountCell,
            minSize: 100,
            maxSize: 140,
            sort: {
                asc: CatalogItemSort.INVOICE_COUNT_ASC,
                desc: CatalogItemSort.INVOICE_COUNT_DESC,
            },
        },
        [CatalogItemColumn.TEMPLATE_COUNT]: {
            accessorKey: "templateCount",
            name: "Templates",
            header: "Templates",
            cell: TemplateCountCell,
            minSize: 100,
            maxSize: 140,
            sort: {
                asc: CatalogItemSort.TEMPLATE_COUNT_ASC,
                desc: CatalogItemSort.TEMPLATE_COUNT_DESC,
            },
        },
        [CatalogItemColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            immutable: true,
            hiddenFromSettings: true,
            cell: ActionsCell,
            minSize: 60,
            maxSize: 60,
        },
    },
});
